import { executeApiCall } from 'services/api';
import { getMutations } from 'entities/hooks/mutation';
import { SuccessData } from 'types/api';
import { ProjectService, RepriceControl } from '../types';
import { uploadMutations } from 'entities/modules/uploads';
import { fileNameToReadable } from 'utils/text';

type AnswerProjectRepriceParams = {
  repriceId: string;
  project: string;
  comment: string;
  repricedServices: Array<{
    id: string;
    name: string;
    service: string;
    outsourceConfig?: ProjectService['outsourceConfig'];

    costs: Array<{
      country: string;
      justification: string;
      costs: ProjectService['costs'][string];
    }>;
  }>;
};

type ApproveProjectRepriceParams = {
  repriceId: string;
  project: string;
  comment: string;

  repricedServices: Array<{
    id: string;
    name: string;
    service: string;
    outsourceConfig?: ProjectService['outsourceConfig'];

    costs: Array<{
      country: string;
      justification: string;
      costs: ProjectService['costs'][string];
      approved?: boolean;
    }>;
  }>;
};

type RequestRepriceParams = {
  comment: string;
  project: string;
  justifications?: RepriceControl;
  reason: string;
  files: File[];
};

export const projectRepriceMutations = getMutations({
  answerProjectReprice: {
    action: async (payload: AnswerProjectRepriceParams): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/reprice',

        method: 'put',

        payload,
      });
    },

    messages: {
      error: (t) => ({
        title: t('error-while-updating-prices'),
        type: 'error',
      }),

      success: (t) => ({
        title: t('prices-updated'),
        type: 'success',
      }),
    },
  },

  acceptProjectReprice: {
    action: async (payload: ApproveProjectRepriceParams): Promise<ProjectService[]> => {
      return executeApiCall({
        path: '/projects/approve-reprice',

        method: 'post',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('project-prices-updated'),
        type: 'success',
      }),
    },
  },

  requestProjectReprice: {
    action: async ({ files, ...payload }: RequestRepriceParams): Promise<SuccessData> => {
      const actualFiles = await Promise.all(
        files.map(async (file) => {
          const { url } = await uploadMutations.uploadFile.action({
            file,
            prefix: `projects/${payload.project}`,
          });

          return {
            url,
            type: file.type || '',
            name: fileNameToReadable(file.name) || 'file',
            filename: file.name,
          };
        }),
      );

      return executeApiCall({
        path: '/projects/reprice',

        method: 'post',

        payload: {
          ...payload,
          files: actualFiles,
        },
      });
    },

    messages: {
      error: (t) => ({
        title: t('error-while-requesting-reprice'),
        type: 'error',
      }),
    },
  },
});
